// Shared SmileSvg — direct port of immersive_gui/ui/src/components/SmileChart.jsx.
// Consumed by the MarketBand "[Forward + Smile]" combined card per the MVP
// MarketBand.jsx pattern — the smile lives INSIDE Market Snapshot, not as its
// own section.
//
// Visual rules verbatim from the MVP:
//   - viewBox 200 × height, preserveAspectRatio="none" — scales to container
//   - x-axis baseline + 5 tick marks (divider colour)
//   - smile curve in Diletta primary cyan, stroke 1.3, rounded caps/joins,
//     vectorEffect="non-scaling-stroke" so width stays 1.3px regardless of scale
//   - filled circle markers (r=2.6) at each point
//   - value labels ABOVE each point in Inter/tabular-nums (7.5px)
//   - x-axis labels BELOW each point (Inter, 8px). ATM (the middle point) is
//     bolder and brighter than the other four.
import { FC } from 'react';
import { useTheme } from '@mui/material';

import { chartsYellow } from '../../../../theme/colors';
import type { SmilePoint } from '../../../../providers/exotics-pricing-provider';

export interface SmileSvgProps {
  smile: SmilePoint[];
  height?: number;
}

const KEYS: SmilePoint['label'][] = ['10P', '25P', 'ATMF', '25C', '10C'];
const LABELS = ['10P', '25P', 'ATM', '25C', '10C'];

const fmtPct = (v: number) =>
  `${(v * 100).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%`;

const SmileSvg: FC<SmileSvgProps> = ({ smile, height = 78 }) => {
  const theme = useTheme();
  const byLabel = new Map(smile.map((p) => [p.label, p.vol] as const));
  const pts = KEYS.map((k) => byLabel.get(k));
  const hasAll = pts.every((v) => typeof v === 'number' && Number.isFinite(v));

  // Layout constants — verbatim from the MVP SmileChart.
  const VB_W = 200;
  const padX = 18;
  const padTop = 14;
  const padBot = 14;
  const axisY = height - 18;

  const vals = pts.filter((v): v is number => typeof v === 'number');
  const min = vals.length ? Math.min(...vals) : 0;
  const max = vals.length ? Math.max(...vals) : 1;
  const range = max - min || 1;

  const xs = pts.map((_, i) => padX + (i / (pts.length - 1)) * (VB_W - 2 * padX));
  const ys = pts.map((v) =>
    typeof v === 'number' ? padTop + (1 - (v - min) / range) * (axisY - padTop - padBot) : axisY,
  );

  // Path skips undefined points by breaking the curve there.
  let path = '';
  let drawing = false;
  pts.forEach((v, i) => {
    if (typeof v !== 'number') {
      drawing = false;
      return;
    }
    path += `${drawing ? 'L' : 'M'} ${xs[i].toFixed(1)} ${ys[i].toFixed(1)} `;
    drawing = true;
  });

  // Amber smile per MVP (SmileChart.jsx default `color = var(--greek-gamma)`
  // = #e3b341). Diletta's chartsYellow (#FAA638) is the closest palette token.
  const curveColour = chartsYellow;
  const axisColour = theme.palette.divider;
  const valueColour = theme.palette.text.primary;
  const labelColour = theme.palette.text.secondary;
  const labelEmphasisColour = theme.palette.text.primary;

  return (
    <svg
      width="100%"
      height={height}
      viewBox={`0 0 ${VB_W} ${height}`}
      preserveAspectRatio="none"
      style={{ display: 'block', overflow: 'visible' }}
    >
      {/* x-axis baseline */}
      <line
        x1={padX - 4}
        y1={axisY}
        x2={VB_W - padX + 4}
        y2={axisY}
        stroke={axisColour}
        strokeWidth="0.6"
      />
      {/* tick marks */}
      {xs.map((x, i) => (
        <line
          key={`tick-${i}`}
          x1={x}
          y1={axisY}
          x2={x}
          y2={axisY + 3}
          stroke={axisColour}
          strokeWidth="0.6"
        />
      ))}
      {/* curve */}
      {hasAll && path && (
        <path
          d={path}
          fill="none"
          stroke={curveColour}
          strokeWidth="1.3"
          strokeLinecap="round"
          strokeLinejoin="round"
          vectorEffect="non-scaling-stroke"
        />
      )}
      {/* points */}
      {xs.map((x, i) =>
        typeof pts[i] === 'number' ? (
          <circle key={`pt-${i}`} cx={x} cy={ys[i]} r="2.6" fill={curveColour} />
        ) : null,
      )}
      {/* value labels above each point */}
      {xs.map((x, i) => {
        const v = pts[i];
        if (typeof v !== 'number') return null;
        return (
          <text
            key={`val-${i}`}
            x={x}
            y={ys[i] - 6}
            textAnchor="middle"
            fontSize="7.5"
            fontFamily="Inter, sans-serif"
            fill={valueColour}
            style={{ fontVariantNumeric: 'tabular-nums' }}
          >
            {fmtPct(v)}
          </text>
        );
      })}
      {/* x-axis labels (ATM is emphasised) */}
      {xs.map((x, i) => (
        <text
          key={`lbl-${i}`}
          x={x}
          y={height - 2}
          textAnchor="middle"
          fontSize="8"
          fontFamily="Inter, sans-serif"
          fontWeight={i === 2 ? 600 : 400}
          fill={i === 2 ? labelEmphasisColour : labelColour}
        >
          {LABELS[i]}
        </text>
      ))}
    </svg>
  );
};

export default SmileSvg;
